<script setup>
import { defineProps, defineEmits, computed } from 'vue'

const props = defineProps({
  owner: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['edit', 'delete'])

const editOwner = () => {
  emit('edit', props.owner.id)
}

const deleteOwner = () => {
  emit('delete', props.owner.id)
}

const isNameComplete = computed(
  () => !!props.owner.firstName && !!props.owner.lastName
)
const isDateOfBirthComplete = computed(() => !!props.owner.dateOfBirth)
const isIdComplete = computed(() => !!props.owner.idImage)
const isAddressComplete = computed(
  () =>
    !!props.owner.addressLine1?.trim() &&
    !!props.owner.addressCity?.trim() &&
    !!props.owner.addressState?.trim() &&
    !!props.owner.addressZip?.trim()
)

const isComplete = computed(
  () =>
    isNameComplete.value &&
    isDateOfBirthComplete.value &&
    isIdComplete.value &&
    isAddressComplete.value
)

// Need to format the date of birth into utc sine that what the server is storing in
const formatDate = (date) => {
  if (!date || !(date instanceof Date)) return null

  // Extract the UTC components of the date
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  const day = date.getUTCDate()

  // Create a new Date object using these components
  const utcDate = new Date(Date.UTC(year, month, day))

  // Format the date
  return utcDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC' // Ensure consistent output regardless of local time zone
  })
}

const idTypeDisplay = computed(() => {
  if (props.owner.idType === 'us_passport') {
    return 'US Passport'
  } else if (props.owner.idType === 'state_drivers_license') {
    return "State Driver's License"
  } else if (props.owner.idType === 'state_id_card') {
    return 'State Issued ID Card'
  } else if (props.owner.idType === 'foreign_passport') {
    return 'Foreign Passport'
  } else {
    return 'Unknown ID Type'
  }
})
</script>

<template>
  <div class="card border-1 shadow-none mb-3" v-if="!owner.deleted">
    <div class="card-body p-4">
      <div class="d-block align-items-center mb-3">
        <h4 class="card-title mb-1">
          {{ owner.firstName }} {{ owner.lastName }}
        </h4>
        <div class="">
          <span
            v-editable
            :class="[
              'badge',
              isComplete
                ? 'bg-soft-success text-success'
                : 'bg-soft-danger text-danger'
            ]"
          >
            <i
              :class="[
                'fa-light',
                isComplete ? 'fa-check-circle' : 'fa-exclamation-circle'
              ]"
            ></i>
            {{ isComplete ? 'Ready to Submit' : 'Incomplete' }}
          </span>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <dl>
            <dt>Name</dt>
            <dd>{{ owner.firstName }} {{ owner.lastName }}</dd>

            <dt>Date of Birth</dt>
            <dd>
              <template v-if="isDateOfBirthComplete">
                {{ formatDate(owner.dateOfBirth) }}
              </template>
              <span v-else class="badge bg-soft-danger text-danger">
                <i class="fa-light fa-exclamation-circle"></i>
                Date of birth is missing
              </span>
            </dd>
          </dl>
        </div>
        <div class="col-md-6">
          <dl>
            <dt>Government ID</dt>
            <dd>
              <template v-if="isIdComplete">{{ idTypeDisplay }}</template>
              <span v-else class="badge bg-soft-danger text-danger">
                <i class="fa-light fa-exclamation-circle"></i>
                Government ID is missing
              </span>
            </dd>

            <dt>Address</dt>
            <dd>
              <template v-if="isAddressComplete">
                {{ owner.addressLine1 }},
                <template v-if="owner.addressLine2"
                  >{{ owner.addressLine2 }},</template
                ><br />
                {{ owner.addressCity }}, {{ owner.addressState }}
                {{ owner.addressZip }}
              </template>
              <span v-else class="badge bg-soft-danger text-danger">
                <i class="fa-light fa-exclamation-circle"></i>
                Missing Address
              </span>
            </dd>
          </dl>
        </div>
      </div>

      <div class="d-flex gap-2 mt-4">
        <button
          v-editable
          @click="editOwner"
          class="btn btn-outline-primary btn-sm"
          type="button"
        >
          <i class="fa-light fa-edit"></i> Edit
        </button>
        <button
          v-editable
          @click="deleteOwner"
          class="btn btn-outline-danger btn-sm"
          type="button"
        >
          <i class="fa-light fa-times"></i> Remove
        </button>
      </div>
    </div>
  </div>
</template>
